import { ProjectDetailsService } from './../../../services/project-details/project-details.service';
import { UserService } from './../../../services/user/user.service';
import { DateUtil } from '../../../DateUtil';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../auth/auth.service';
import { TaskService } from '../../../services/task/task.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TaskStatus, taskStatusToLabel } from '../../../model/task/taskStatus';
import { TaskLabel, taskLabelToLabel } from '../../../model/task/taskLabel';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { TeamMember } from '../../../model/team/TeamMember';
import { Component, Inject, OnInit } from '@angular/core';
import { Task } from 'src/app/shared/model/task/task';
import { GetProjects } from 'src/app/shared/services/project-list/GetProjects';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { ValidatorUtil } from 'src/app/shared/ValidatorUtil';

@Component({
  selector: 'digires-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss'],
})
export class TaskDetailsComponent implements OnInit {
  taskDetailFormGroup: FormGroup;
  labels = Object.keys(TaskLabel);
  statusList = Object.keys(TaskStatus).filter((s) => s !== TaskStatus.DELETED);
  task: Task;
  isNewTask: boolean;
  project: GetProjects;
  isReadOnly = false;

  createdByUsername: string;
  updatedByUsername: string;
  teamMembers: TeamMember[];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      task: Task;
      project: GetProjects;
      teamMembers: TeamMember[];
    },
    private dialogRef: MatDialogRef<TaskDetailsComponent>,
    private userService: UserService,
    private fb: FormBuilder,
    private taskService: TaskService,
    private authService: AuthService,
    private dialog: MatDialog,
    private projectDetailsService: ProjectDetailsService,
  ) {
    this.task = data.task;
    this.project = data.project;
  }

  async ngOnInit() {
    if (!this.task) {
      this.task = new Task();
      if (this.data.teamMembers.length === 1) {
        const projectMember: TeamMember = this.data.teamMembers[0];
        this.task.ownerId = projectMember.userId;
      }
      this.isNewTask = true;
      this.task.status = TaskStatus.TO_DO;
    }

    this.isReadOnly = !this.authService.canEditTaskItem(this.project, this.task);
    const date = DateUtil.formField(this.task.dueDate);
    let member: TeamMember = null;
    // this.userService.loadUserById(this.task.ownerId);
    this.teamMembers = this.data.teamMembers;

    if (!this.teamMembers || this.teamMembers.length === 0) {
      const projectId = this.task.projectId || this.project.projectId;
      this.teamMembers = (await this.projectDetailsService.fetchTeam(projectId)).members;
    }

    for (const pm of this.teamMembers) {
      if (pm.userId === this.task.ownerId) {
        member = pm;
      }
    }

    this.taskDetailFormGroup = this.fb.group({
      name: new FormControl(this.task.name, Validators.required),
      status: new FormControl({ value: this.task.status, disabled: this.isNewTask }, Validators.required),
      details: new FormControl(this.task.details),
      owner: new FormControl(member, Validators.required),
      dueDate: new FormControl(date, Validators.compose([Validators.required, ValidatorUtil.validateDate])),
      label: new FormControl(this.task.label, Validators.required),
    });

    if (this.task.createdBy) {
      this.createdByUsername = (await this.userService.loadUserById(this.task.createdBy)).fullName;
    }
    if (this.task.updatedBy) {
      this.updatedByUsername = (await this.userService.loadUserById(this.task.updatedBy)).fullName;
    }
  }

  hasInvalidDate(): boolean {
    return !!(
      this.taskDetailFormGroup.controls.dueDate.errors?.invalid_date
    );
  }

  isOutOfRange(): boolean {
    return !!(
      this.taskDetailFormGroup.controls.dueDate.errors?.date_out_of_range
    );
  }

  updateTask(): void {
    if (this.taskDetailFormGroup.valid) {
      this.task.name = this.taskDetailFormGroup.value.name;
      this.task.status = this.taskDetailFormGroup.getRawValue().status;
      this.task.details = this.taskDetailFormGroup.value.details;
      this.task.ownerId = this.taskDetailFormGroup.value.owner.userId;
      this.task.dueDate = DateUtil.anyToDate(this.taskDetailFormGroup.value.dueDate);
      this.task.label = this.taskDetailFormGroup.value.label;

      if (this.isNewTask && this.project) {
        this.task.projectId = this.project.projectId;
        this.taskService.addTask(this.task);
      } else {
        this.taskService.updateTask(this.task);
      }
    }
  }

  showDeleteButton() {
    return !this.isNewTask && this.authService.canEditTaskItem(this.project, this.task);
  }

  deleteTask() {
    firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          hasBackdrop: true,
          data: {
            title: 'Delete Task',
            message: 'Are you sure that you want to delete this task?',
            actions: [
              {
                id: 'abort',
                label: 'Cancel',
                tooltip: 'Returns without deleting the task',
                disabled: false,
                flair: 'neutral',
              },
              {
                id: 'ok',
                label: 'Delete',
                tooltip: 'Deletes the Task',
                disabled: false,
                flair: 'negative',
              },
            ],
          },
        })
        .afterClosed(),
    ).then((result) => {
      if (result === 'ok') {
        this.task.status = TaskStatus.DELETED;
        this.taskService.updateTask(this.task);
      }
      this.dialogRef.close(true);
    });
  }

  statusToLabel(status: TaskStatus) {
    return taskStatusToLabel[status];
  }

  labelToLabel(label: TaskLabel) {
    return taskLabelToLabel[label];
  }
}
